import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Challenge, Participant } from '@/types';
import { Card } from '@/components/ui/Card';
import { useUserStore } from '@/stores/userStore';
import {
  getExerciseForChallenge,
  isChallengeCompleted,
  isChallengeFailed,
  getCurrentDate,
  calculateDailyTarget,
  getDaysRemaining,
} from '@/lib/utils';
import { ChallengeCompletion } from './ChallengeCompletion';
import { useBatchParticipants } from '@/hooks/useBatchParticipants';

interface ChallengesSectionsProps {
  challenges: Challenge[];
  onSelectChallenge: (challenge: Challenge) => void;
}

interface ChallengeCardProps {
  challenge: Challenge;
  onClick: () => void;
  isCompleted?: boolean;
  isFailed?: boolean;
  participants?: Participant[];
  isLoading?: boolean;
}

const ChallengeCard: React.FC<ChallengeCardProps> = React.memo(
  ({
    challenge,
    onClick,
    isCompleted = false,
    isFailed = false,
    participants,
    isLoading = false,
  }) => {
    const { user } = useUserStore();

    // Memoize exercise calculation - must be before any early returns
    const exercise = useMemo(() => getExerciseForChallenge(challenge), [challenge]);

    // Memoize all values that might be needed - must be unconditional
    const today = useMemo(() => getCurrentDate(), []);
    const userParticipant = participants?.find(p => p.userId === user?.id);
    const todayProgress = userParticipant?.dailyLog?.[today] || 0;

    // Calculate adaptive daily target using the utility function
    const dailyTarget = useMemo(
      () => (userParticipant ? calculateDailyTarget(challenge, userParticipant) : 0),
      [challenge, userParticipant]
    );

    // Calculate days remaining
    const daysLeft = useMemo(
      () => getDaysRemaining(today, challenge.endDate),
      [today, challenge.endDate]
    );

    const daysLeftText = useMemo(
      () =>
        daysLeft === 0
          ? 'Last day!'
          : daysLeft === 1
            ? '1 day left'
            : daysLeft < 0
              ? 'Ended'
              : `${daysLeft} days left`,
      [daysLeft]
    );

    // Show functional card with loading indication while participants load
    if (isLoading || !participants) {
      return (
        <Card
          className="p-4 cursor-pointer hover:shadow-lg transition-shadow border-l-4 border-blue-400"
          onClick={onClick}
          onKeyDown={(e: React.KeyboardEvent) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              onClick();
            }
          }}
          tabIndex={0}
          role="button"
          aria-label={`View challenge: ${challenge.name} (Loading...)`}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="text-2xl">
                {challenge.exerciseType === 'custom'
                  ? challenge.customExercise?.icon || '💪'
                  : exercise.icon}
              </div>
              <div>
                <h3 className="font-semibold text-gray-800">{challenge.name}</h3>
                <p className="text-sm text-gray-500">
                  {challenge.target.toLocaleString()} {exercise.unit} challenge
                </p>
                <div className="flex items-center gap-2 mt-1">
                  <div className="w-2 h-2 bg-blue-400 rounded-full animate-pulse"></div>
                  <span className="text-xs text-blue-600">Loading progress...</span>
                </div>
              </div>
            </div>
            <div className="text-gray-400">→</div>
          </div>
        </Card>
      );
    }

    return (
      <Card
        className={`p-4 cursor-pointer hover:shadow-lg transition-shadow ${
          isCompleted ? 'border-green-200 bg-green-50' : isFailed ? 'border-red-200 bg-red-50' : ''
        }`}
        onClick={onClick}
        onKeyDown={(e: React.KeyboardEvent) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onClick();
          }
        }}
        tabIndex={0}
        role="button"
        aria-label={`View challenge: ${challenge.name}${isCompleted ? ' (Completed)' : isFailed ? ' (Failed)' : ''}`}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="text-2xl flex items-center">
              {exercise.icon}
              {isCompleted && <span className="text-lg ml-1">🏆</span>}
              {isFailed && <span className="text-lg ml-1">💥</span>}
            </div>
            <div className="flex-1">
              <h3
                className={`font-semibold ${
                  isCompleted ? 'text-green-800' : isFailed ? 'text-red-800' : 'text-gray-800'
                }`}
              >
                {challenge.name}
                {isCompleted && (
                  <span className="text-sm text-green-600 ml-2 font-normal">COMPLETED</span>
                )}
                {isFailed && <span className="text-sm text-red-600 ml-2 font-normal">FAILED</span>}
              </h3>
              <div className="flex items-center gap-4 text-sm">
                <p className="text-gray-500">
                  {userParticipant
                    ? `${userParticipant.totalAmount.toLocaleString()}/${challenge.target.toLocaleString()} ${exercise.unit}`
                    : 'Not joined'}
                </p>
                {userParticipant && !isCompleted && !isFailed && (
                  <div
                    className={`flex items-center gap-1 ${todayProgress >= dailyTarget ? 'text-green-600' : 'text-orange-600'}`}
                  >
                    <span className="font-medium">Today:</span>
                    <span>
                      {todayProgress}/{dailyTarget}
                    </span>
                    {todayProgress >= dailyTarget && <span>✓</span>}
                  </div>
                )}
              </div>
              {/* Days remaining */}
              <div className="text-xs mt-1">
                <span
                  className={`font-medium ${
                    daysLeft === 0
                      ? 'text-red-600'
                      : daysLeft <= 3
                        ? 'text-orange-600'
                        : isCompleted
                          ? 'text-green-600'
                          : isFailed
                            ? 'text-red-600'
                            : 'text-gray-600'
                  }`}
                >
                  {isCompleted ? '✅ Completed' : isFailed ? '❌ Failed' : `⏱️ ${daysLeftText}`}
                </span>
              </div>
            </div>
          </div>
          <div
            className={isCompleted ? 'text-green-600' : isFailed ? 'text-red-600' : 'text-gray-400'}
          >
            →
          </div>
        </div>

        {userParticipant && (
          <div className="mt-3 space-y-2">
            <div
              className="w-full bg-gray-200 rounded-full h-2"
              role="progressbar"
              aria-valuemin={0}
              aria-valuemax={challenge.target}
              aria-valuenow={userParticipant.totalAmount}
              aria-label={`Challenge progress: ${userParticipant.totalAmount} of ${challenge.target} ${getExerciseForChallenge(challenge).unit}`}
            >
              <div
                className={`h-2 rounded-full transition-all duration-300 ${
                  isCompleted
                    ? 'bg-gradient-to-r from-green-400 to-blue-500'
                    : isFailed
                      ? 'bg-gradient-to-r from-red-400 to-red-600'
                      : 'bg-gradient-to-r from-blue-400 to-purple-500'
                }`}
                style={{
                  width: `${Math.min(100, (userParticipant.totalAmount / challenge.target) * 100)}%`,
                }}
              />
            </div>
            {!isCompleted && !isFailed && (
              <div
                className="w-full bg-gray-100 rounded-full h-1.5"
                role="progressbar"
                aria-valuemin={0}
                aria-valuemax={dailyTarget}
                aria-valuenow={todayProgress}
                aria-label={`Today's progress: ${todayProgress} of ${dailyTarget} ${getExerciseForChallenge(challenge).unit}`}
              >
                <div
                  className={`h-1.5 rounded-full transition-all duration-300 ${
                    todayProgress >= dailyTarget ? 'bg-green-500' : 'bg-orange-400'
                  }`}
                  style={{
                    width: `${Math.min(100, (todayProgress / dailyTarget) * 100)}%`,
                  }}
                />
              </div>
            )}
          </div>
        )}
      </Card>
    );
  },
  (prevProps, nextProps) => {
    // Custom comparison to prevent unnecessary re-renders
    return (
      prevProps.challenge.id === nextProps.challenge.id &&
      prevProps.challenge.updatedAt === nextProps.challenge.updatedAt &&
      prevProps.isCompleted === nextProps.isCompleted &&
      prevProps.isFailed === nextProps.isFailed &&
      prevProps.isLoading === nextProps.isLoading &&
      JSON.stringify(prevProps.participants) === JSON.stringify(nextProps.participants)
    );
  }
);

const ChallengesSections: React.FC<ChallengesSectionsProps> = ({
  challenges,
  onSelectChallenge,
}) => {
  const { user } = useUserStore();
  const [activeChallenges, setActiveChallenges] = useState<Challenge[]>([]);
  const [completedChallenges, setCompletedChallenges] = useState<Challenge[]>([]);
  const [failedChallenges, setFailedChallenges] = useState<Challenge[]>([]);
  const [showCompletion, setShowCompletion] = useState<{
    challenge: Challenge;
    participant: Participant;
  } | null>(null);
  const [completedChallengeIds, setCompletedChallengeIds] = useState<Set<string>>(new Set());
  const [celebratedChallenges, setCelebratedChallenges] = useState<Set<string>>(() => {
    // Load celebrated challenges from localStorage
    const stored = localStorage.getItem('celebratedChallenges');
    return stored ? new Set(JSON.parse(stored)) : new Set();
  });
  const [, forceUpdate] = useState({});

  // Get participant data for all challenges using batch fetching
  const { participantsByChallenge } = useBatchParticipants(challenges);

  // Force re-render at midnight to update today's progress
  const checkDateChange = useCallback(() => {
    forceUpdate({});
  }, []);

  useEffect(() => {
    const setupMidnightTimer = () => {
      const now = new Date();
      const msUntilMidnight =
        (24 - now.getHours()) * 60 * 60 * 1000 -
        now.getMinutes() * 60 * 1000 -
        now.getSeconds() * 1000 -
        now.getMilliseconds();

      // Set timeout to force update at midnight
      const timeout = setTimeout(() => {
        checkDateChange();
        setupMidnightTimer(); // Schedule next check
      }, msUntilMidnight);

      return timeout;
    };

    const timeout = setupMidnightTimer();
    return () => clearTimeout(timeout);
  }, [checkDateChange]);

  useEffect(() => {
    const active: Challenge[] = [];
    const completed: Challenge[] = [];
    const failed: Challenge[] = [];
    const newlyCompleted: { challenge: Challenge; participant: Participant }[] = [];

    challenges.forEach(challenge => {
      const participants = participantsByChallenge.get(challenge.id);

      if (!participants || !user) {
        active.push(challenge);
        return;
      }

      const userParticipant = participants.find((p: Participant) => p.userId === user.id);
      if (!userParticipant) {
        active.push(challenge);
        return;
      }

      const isCompleted = isChallengeCompleted(challenge, userParticipant);
      const isFailed = isChallengeFailed(challenge, userParticipant);

      if (isCompleted) {
        completed.push(challenge);

        // Only show celebration if this challenge just transitioned from incomplete to complete
        // (was not completed before but is completed now) AND hasn't been celebrated yet
        if (!completedChallengeIds.has(challenge.id) && !celebratedChallenges.has(challenge.id)) {
          // Only add to newly completed if we have previous state (not initial load)
          // This prevents showing celebration on initial page load for already completed challenges
          if (completedChallengeIds.size > 0) {
            newlyCompleted.push({ challenge, participant: userParticipant });
          }
        }
      } else if (isFailed) {
        failed.push(challenge);
      } else {
        active.push(challenge);
      }
    });

    setActiveChallenges(active);
    setCompletedChallenges(completed);
    setFailedChallenges(failed);

    // Update completed IDs set
    const newCompletedIds = new Set(completed.map(c => c.id));
    setCompletedChallengeIds(newCompletedIds);

    // Show completion celebration for newly completed challenges
    if (newlyCompleted.length > 0) {
      // Show the first newly completed challenge
      setShowCompletion(newlyCompleted[0]);
    }
  }, [challenges, participantsByChallenge, user, celebratedChallenges, completedChallengeIds]);

  const handleCompletionClose = () => {
    if (showCompletion) {
      // Mark this challenge as celebrated
      const newCelebrated = new Set(celebratedChallenges);
      newCelebrated.add(showCompletion.challenge.id);
      setCelebratedChallenges(newCelebrated);

      // Persist to localStorage
      localStorage.setItem('celebratedChallenges', JSON.stringify(Array.from(newCelebrated)));
    }
    setShowCompletion(null);
  };

  // Show skeleton loading state if we have no participant data loaded yet OR if we have no challenges at all
  const showLoadingSkeleton = challenges.length === 0 || participantsByChallenge.size === 0;

  return (
    <>
      <div className="mt-10 space-y-8">
        {/* Loading Skeleton */}
        {showLoadingSkeleton && (
          <div>
            <h2 className="text-2xl font-bold text-slate-800 mb-6">Loading Challenges...</h2>
            <div className="space-y-4">
              {(challenges.length > 0 ? challenges.slice(0, 3) : Array(3).fill(null)).map((challenge, index) => (
                <div key={challenge?.id || `skeleton-${index}`} className="animate-pulse">
                  <div className="bg-white rounded-lg border border-gray-200 p-4 shadow-sm">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <div className="w-8 h-8 bg-gray-300 rounded-full"></div>
                        <div>
                          <div className="h-4 bg-gray-300 rounded w-40 mb-2"></div>
                          <div className="h-3 bg-gray-200 rounded w-32"></div>
                        </div>
                      </div>
                      <div className="w-4 h-4 bg-gray-300 rounded"></div>
                    </div>
                    <div className="mt-3">
                      <div className="w-full bg-gray-200 rounded-full h-2 mb-2"></div>
                      <div className="w-3/4 bg-gray-200 rounded-full h-1.5"></div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Active Challenges */}
        {!showLoadingSkeleton && activeChallenges.length > 0 && (
          <div>
            <h2 className="text-2xl font-bold text-slate-800 mb-6">Active Challenges</h2>
            <div className="space-y-4">
              {activeChallenges.map(challenge => (
                <ChallengeCard
                  key={challenge.id}
                  challenge={challenge}
                  onClick={() => onSelectChallenge(challenge)}
                  isCompleted={false}
                  participants={participantsByChallenge.get(challenge.id)}
                  isLoading={!participantsByChallenge.has(challenge.id)}
                />
              ))}
            </div>
          </div>
        )}

        {/* Completed Challenges */}
        {completedChallenges.length > 0 && (
          <div>
            <h2 className="text-2xl font-bold text-slate-800 mb-6 flex items-center gap-2">
              <span>🏆</span>
              <span>Completed Challenges</span>
            </h2>
            <div className="space-y-4">
              {completedChallenges.map(challenge => (
                <ChallengeCard
                  key={challenge.id}
                  challenge={challenge}
                  onClick={() => onSelectChallenge(challenge)}
                  isCompleted={true}
                  participants={participantsByChallenge.get(challenge.id)}
                  isLoading={!participantsByChallenge.has(challenge.id)}
                />
              ))}
            </div>
          </div>
        )}

        {/* Failed Challenges */}
        {failedChallenges.length > 0 && (
          <div>
            <h2 className="text-2xl font-bold text-slate-800 mb-6 flex items-center gap-2">
              <span>💥</span>
              <span>Failed Challenges</span>
            </h2>
            <div className="space-y-4">
              {failedChallenges.map(challenge => (
                <ChallengeCard
                  key={challenge.id}
                  challenge={challenge}
                  onClick={() => onSelectChallenge(challenge)}
                  isCompleted={false}
                  isFailed={true}
                  participants={participantsByChallenge.get(challenge.id)}
                  isLoading={!participantsByChallenge.has(challenge.id)}
                />
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Completion Celebration Modal */}
      {showCompletion && (
        <ChallengeCompletion
          challenge={showCompletion.challenge}
          participant={showCompletion.participant}
          onClose={handleCompletionClose}
          onViewChallenge={() => {
            handleCompletionClose();
            onSelectChallenge(showCompletion.challenge);
          }}
        />
      )}
    </>
  );
};

export { ChallengesSections };
